import { useState, useEffect } from 'react';
import { contractorApi } from '../../lib/contractorApi';

interface Slot {
  id: string;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string;
  end_time: string;
  is_blocked: number;
  notes: string | null;
}

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const TIMES = ['07:00', '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'];

function fmt12(t: string): string {
  const [h, m] = t.split(':').map(Number);
  const suffix = (h ?? 0) >= 12 ? 'pm' : 'am';
  const h12 = (h ?? 0) % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')}${suffix}`;
}

interface AddSlotForm {
  day_of_week: string;
  specific_date: string;
  start_time: string;
  end_time: string;
  is_blocked: boolean;
  notes: string;
}

const DEFAULT_FORM: AddSlotForm = {
  day_of_week: '',
  specific_date: '',
  start_time: '08:00',
  end_time: '17:00',
  is_blocked: false,
  notes: '',
};

export function Availability() {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState<AddSlotForm>(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [view, setView] = useState<'recurring' | 'specific'>('recurring');

  useEffect(() => {
    contractorApi.get<{ data: Slot[] }>('/contractor/availability')
      .then(d => setSlots(d.data ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function addSlot() {
    setSaving(true);
    setError(null);
    const body: Record<string, unknown> = {
      start_time: form.start_time,
      end_time: form.end_time,
      is_blocked: form.is_blocked,
    };
    if (form.notes.trim()) body.notes = form.notes.trim();

    if (view === 'recurring' && form.day_of_week !== '') {
      body.day_of_week = parseInt(form.day_of_week);
    } else if (view === 'specific' && form.specific_date) {
      body.specific_date = form.specific_date;
    } else {
      setError('Select a day of the week or a specific date.');
      setSaving(false);
      return;
    }

    if (form.start_time >= form.end_time) {
      setError('Start time must be before end time.');
      setSaving(false);
      return;
    }

    try {
      const d = await contractorApi.post<{ data: Slot }>('/contractor/availability', body);
      setSlots(prev => [...prev, d.data]);
      setForm(DEFAULT_FORM);
      setShowAdd(false);
    } catch {
      setError('Failed to save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  async function deleteSlot(id: string) {
    setDeleting(id);
    try {
      await contractorApi.delete<unknown>(`/contractor/availability/${id}`);
      setSlots(prev => prev.filter(s => s.id !== id));
    } catch {
      // silently ignore
    } finally {
      setDeleting(null);
    }
  }

  const recurring = slots.filter(s => s.day_of_week !== null && s.specific_date === null);
  const specific = slots.filter(s => s.specific_date !== null);

  const recurringByDow = DOW_LABELS.map((label, dow) => ({
    dow,
    label,
    slots: recurring.filter(s => s.day_of_week === dow),
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Availability</h1>
          <p className="text-sm text-gray-500 mt-0.5">Homeowners see your open slots when they accept a lead.</p>
        </div>
        <button
          onClick={() => setShowAdd(v => !v)}
          className="flex items-center gap-1.5 text-sm font-medium bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add slot
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setView('recurring')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'recurring' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Recurring
            </button>
            <button
              onClick={() => setView('specific')}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${view === 'specific' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              Specific date
            </button>
          </div>

          {view === 'recurring' ? (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Day of week</label>
              <div className="flex gap-1.5 flex-wrap">
                {DOW_LABELS.map((label, idx) => (
                  <button
                    key={idx}
                    onClick={() => setForm(f => ({ ...f, day_of_week: String(idx) }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      form.day_of_week === String(idx)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={form.specific_date}
                onChange={e => setForm(f => ({ ...f, specific_date: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">Start time</label>
              <select
                value={form.start_time}
                onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TIMES.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">End time</label>
              <select
                value={form.end_time}
                onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TIMES.map(t => <option key={t} value={t}>{fmt12(t)}</option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="is-blocked"
              type="checkbox"
              checked={form.is_blocked}
              onChange={e => setForm(f => ({ ...f, is_blocked: e.target.checked }))}
              className="h-4 w-4 text-red-600 rounded border-gray-300"
            />
            <label htmlFor="is-blocked" className="text-sm text-gray-700">Mark as unavailable / blocked</label>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Note (optional)</label>
            <input
              type="text"
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              placeholder="e.g. Afternoon only"
              maxLength={200}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex gap-2">
            <button
              onClick={() => { setShowAdd(false); setError(null); setForm(DEFAULT_FORM); }}
              className="flex-1 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={addSlot}
              disabled={saving}
              className="flex-1 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700"
            >
              {saving ? 'Saving…' : 'Save slot'}
            </button>
          </div>
        </div>
      )}

      {/* Recurring schedule grid */}
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Recurring schedule</h2>
        <div className="space-y-2">
          {recurringByDow.map(({ label, slots: dowSlots }) => (
            <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 w-10">{label}</span>
                {dowSlots.length === 0
                  ? <span className="text-xs text-gray-400">No slots</span>
                  : (
                    <div className="flex flex-wrap gap-2">
                      {dowSlots.map(s => (
                        <div key={s.id} className={`flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium ${s.is_blocked ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}>
                          {fmt12(s.start_time)} – {fmt12(s.end_time)}
                          {s.notes && <span className="text-gray-400">· {s.notes}</span>}
                          <button
                            onClick={() => deleteSlot(s.id)}
                            disabled={deleting === s.id}
                            className="ml-1 text-gray-400 hover:text-red-500"
                            aria-label="Remove slot"
                          >
                            {deleting === s.id ? '…' : '×'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                }
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Specific date overrides */}
      {specific.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-3">Date overrides</h2>
          <div className="space-y-2">
            {specific.map(s => (
              <div key={s.id} className="bg-white border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(s.specific_date + 'T12:00:00').toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className={`text-xs ${s.is_blocked ? 'text-red-600' : 'text-green-600'}`}>
                    {s.is_blocked ? 'Blocked' : `${fmt12(s.start_time)} – ${fmt12(s.end_time)}`}
                    {s.notes && ` · ${s.notes}`}
                  </p>
                </div>
                <button
                  onClick={() => deleteSlot(s.id)}
                  disabled={deleting === s.id}
                  className="text-gray-400 hover:text-red-500 text-sm"
                  aria-label="Remove"
                >
                  {deleting === s.id ? '…' : 'Remove'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {slots.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-sm">No availability set yet.</p>
          <p className="text-xs mt-1">Add your recurring schedule so homeowners know when you're free.</p>
        </div>
      )}
    </div>
  );
}
